import api from './api';

export interface CalendarEvent {
  id: string;
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone?: string;
  };
  end: {
    dateTime: string;
    timeZone?: string;
  };
  location?: string;
  attendees?: Array<{
    email: string;
    responseStatus?: string;
    displayName?: string;
  }>;
  htmlLink: string;
  status: string;
  creator?: {
    email: string;
    displayName?: string;
  };
  organizer?: {
    email: string;
    displayName?: string;
  };
}

export interface CalendarEventCreate {
  summary: string;
  description?: string;
  start: {
    dateTime: string;
    timeZone?: string;
  };
  end: {
    dateTime: string;
    timeZone?: string;
  };
  location?: string;
  attendees?: Array<{
    email: string;
  }>;
  reminders?: {
    useDefault?: boolean;
    overrides?: Array<{
      method: 'email' | 'popup';
      minutes: number;
    }>;
  };
  calendarId?: string;
}

export interface CalendarSettings {
  enabled: boolean;
  defaultCalendarId: string;
  syncInterviews: boolean;
  syncFollowUps: boolean;
  reminderMinutes: number;
}

export interface Calendar {
  id: string;
  summary: string;
  description?: string;
  primary?: boolean;
  accessRole: string;
  selected?: boolean;
  backgroundColor?: string;
  foregroundColor?: string;
}

class CalendarService {
  /**
   * Check if user has calendar access
   */
  async checkAccess(): Promise<{ hasAccess: boolean; settings: CalendarSettings }> {
    const response = await api.get('/api/calendar/access');
    return response.data.data;
  }

  /**
   * Get user's calendars
   */
  async getCalendars(): Promise<Calendar[]> {
    const response = await api.get('/api/calendar/calendars');
    return response.data.data;
  }

  /**
   * Get calendar events
   */
  async getEvents(params?: {
    calendarId?: string;
    timeMin?: string;
    timeMax?: string;
    maxResults?: number;
  }): Promise<CalendarEvent[]> {
    const response = await api.get('/api/calendar/events', { params });
    return response.data.data;
  }

  /**
   * Get upcoming events (next 7 days)
   */
  async getUpcomingEvents(): Promise<CalendarEvent[]> {
    const response = await api.get('/api/calendar/upcoming');
    return response.data.data;
  }

  /**
   * Create a new calendar event
   */
  async createEvent(eventData: CalendarEventCreate): Promise<CalendarEvent> {
    const response = await api.post('/api/calendar/events', eventData);
    return response.data.data;
  }

  /**
   * Update an existing calendar event
   */
  async updateEvent(eventId: string, eventData: Partial<CalendarEventCreate>): Promise<CalendarEvent> {
    const response = await api.put(`/api/calendar/events/${eventId}`, eventData);
    return response.data.data;
  }

  /**
   * Delete a calendar event
   */
  async deleteEvent(eventId: string, calendarId?: string): Promise<void> {
    const params = calendarId ? { calendarId } : {};
    await api.delete(`/api/calendar/events/${eventId}`, { params });
  }

  /**
   * Get calendar settings
   */
  async getSettings(): Promise<CalendarSettings> {
    const response = await api.get('/api/calendar/settings');
    return response.data.data;
  }

  /**
   * Update calendar settings
   */
  async updateSettings(settings: Partial<CalendarSettings>): Promise<CalendarSettings> {
    const response = await api.put('/api/calendar/settings', settings);
    return response.data.data;
  }

  /**
   * Create an interview event from application data
   */
  async createInterviewEvent(applicationData: {
    companyName: string;
    position?: string;
    interviewType: string;
    startTime: string;
    endTime: string;
    location?: string;
    interviewerEmail?: string;
    notes?: string;
  }): Promise<CalendarEvent> {
    const eventData: CalendarEventCreate = {
      summary: `${applicationData.interviewType} Interview - ${applicationData.companyName}`,
      description: `Interview for ${applicationData.position ? applicationData.position + ' at ' : ''}${applicationData.companyName}\n\n${applicationData.notes || ''}`,
      start: {
        dateTime: applicationData.startTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: applicationData.endTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      location: applicationData.location,
      attendees: applicationData.interviewerEmail ? [{ email: applicationData.interviewerEmail }] : undefined
    };

    return this.createEvent(eventData);
  }

  /**
   * Create a follow-up reminder event
   */
  async createFollowUpEvent(followUpData: {
    companyName: string;
    followUpDate: string;
    notes?: string;
  }): Promise<CalendarEvent> {
    const followUpDateTime = new Date(followUpData.followUpDate);
    const endDateTime = new Date(followUpDateTime.getTime() + 30 * 60 * 1000); // 30 minutes

    const eventData: CalendarEventCreate = {
      summary: `Follow up - ${followUpData.companyName}`,
      description: `Follow up with ${followUpData.companyName}\n\n${followUpData.notes || ''}`,
      start: {
        dateTime: followUpDateTime.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: endDateTime.toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };

    return this.createEvent(eventData);
  }

  /**
   * Format date for calendar display
   */
  formatEventDate(dateTime: string): string {
    const date = new Date(dateTime);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  /**
   * Get events for a specific day
   */
  async getEventsForDay(date: Date): Promise<CalendarEvent[]> {
    const timeMin = new Date(date);
    timeMin.setHours(0, 0, 0, 0);
    
    const timeMax = new Date(date);
    timeMax.setHours(23, 59, 59, 999);

    return this.getEvents({
      timeMin: timeMin.toISOString(),
      timeMax: timeMax.toISOString()
    });
  }

  /**
   * Get events for current week
   */
  async getWeekEvents(): Promise<CalendarEvent[]> {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    return this.getEvents({
      timeMin: startOfWeek.toISOString(),
      timeMax: endOfWeek.toISOString()
    });
  }

  /**
   * Get upcoming interview events only (next 7 days)
   */
  async getUpcomingInterviews(): Promise<CalendarEvent[]> {
    const response = await api.get('/api/calendar/interviews/upcoming');
    return response.data.data;
  }

  /**
   * Get interview events by type
   */
  async getInterviewsByType(type: string, params?: {
    timeMin?: string;
    timeMax?: string;
    maxResults?: number;
  }): Promise<CalendarEvent[]> {
    const queryParams = new URLSearchParams();
    if (params?.timeMin) queryParams.append('timeMin', params.timeMin);
    if (params?.timeMax) queryParams.append('timeMax', params.timeMax);
    if (params?.maxResults) queryParams.append('maxResults', params.maxResults.toString());
    
    const url = `/api/calendar/interviews/type/${type}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await api.get(url);
    return response.data.data;
  }

  /**
   * Get interview statistics
   */
  async getInterviewStats(params?: {
    timeMin?: string;
    timeMax?: string;
  }): Promise<{
    totalInterviews: number;
    upcomingInterviews: number;
    completedInterviews: number;
    byType: {
      phone: number;
      video: number;
      onsite: number;
      technical: number;
      behavioral: number;
      final: number;
      other: number;
    };
    companies: number;
  }> {
    const queryParams = new URLSearchParams();
    if (params?.timeMin) queryParams.append('timeMin', params.timeMin);
    if (params?.timeMax) queryParams.append('timeMax', params.timeMax);
    
    const url = `/api/calendar/interviews/stats${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    const response = await api.get(url);
    return response.data.data;
  }
}

const calendarService = new CalendarService();
export default calendarService; 